"use client";

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { webSiteNavigationLabels } from "../webSiteNavigation";
import { useWebMobileMenuOverlay } from "./useWebMobileMenuOverlay";

export function RestaurantMobileMenu({
    languageCode,
    templateCode,
}: {
    languageCode?: string | null;
    templateCode: string;
}) {
    const navigationId = useId();
    const sentinelRef = useRef<HTMLSpanElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const [header, setHeader] = useState<HTMLElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const labels = webSiteNavigationLabels(languageCode);

    useEffect(() => {
        const template = sentinelRef.current?.closest<HTMLElement>(
            `[data-web-template="${templateCode}"][data-web-template-family="restaurant"]`,
        );
        const nextHeader = template?.querySelector<HTMLElement>(
            '[data-web-region="header"]',
        ) ?? null;
        const hero = template?.querySelector<HTMLElement>(
            '[data-web-region="hero"]',
        ) ?? null;
        const runtimeWindow = template?.ownerDocument.defaultView ?? null;

        setHeader(nextHeader);

        if (!template || !nextHeader || !runtimeWindow) {
            return;
        }

        let animationFrame = 0;
        const updateHeader = () => {
            runtimeWindow.cancelAnimationFrame(animationFrame);
            animationFrame = runtimeWindow.requestAnimationFrame(() => {
                const isMobile = template.getBoundingClientRect().width <= 900;
                const threshold = isMobile
                    ? 54
                    : templateCode === "restaurant-modern" && hero
                        ? Math.max(110, hero.offsetHeight - 80)
                        : 110;

                nextHeader.toggleAttribute(
                    "data-restaurant-header-scrolled",
                    hero === null || runtimeWindow.scrollY > threshold,
                );
            });
        };

        updateHeader();
        runtimeWindow.addEventListener("resize", updateHeader, { passive: true });
        runtimeWindow.addEventListener("scroll", updateHeader, { passive: true });

        const sections = Array.from(template.querySelectorAll<HTMLElement>(
            '[data-builder-document-region="page"] .site-builder-section',
        ));
        const editorMode = template.dataset.webTemplateEditor === "true";
        const reducedMotion = runtimeWindow.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let observer: IntersectionObserver | null = null;

        sections.forEach((section, index) => {
            section.dataset.restaurantReveal = "true";
            if (editorMode || reducedMotion || index === 0) {
                section.dataset.restaurantRevealed = "true";
            }
        });

        if (!editorMode && !reducedMotion && runtimeWindow.IntersectionObserver) {
            observer = new runtimeWindow.IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    (entry.target as HTMLElement).dataset.restaurantRevealed = "true";
                    observer?.unobserve(entry.target);
                });
            }, {
                rootMargin: "0px 0px -8%",
                threshold: 0.12,
            });

            sections.slice(1).forEach((section) => observer?.observe(section));
        } else {
            sections.forEach((section) => {
                section.dataset.restaurantRevealed = "true";
            });
        }

        return () => {
            runtimeWindow.cancelAnimationFrame(animationFrame);
            runtimeWindow.removeEventListener("resize", updateHeader);
            runtimeWindow.removeEventListener("scroll", updateHeader);
            observer?.disconnect();
            nextHeader.removeAttribute("data-restaurant-header-scrolled");
        };
    }, [templateCode]);

    useWebMobileMenuOverlay({
        header,
        isOpen,
        navigationId,
        openAttribute: "data-restaurant-mobile-menu-open",
        setIsOpen,
        templateCode,
        toggleRef,
    });

    return (
        <>
            <span
                aria-hidden="true"
                data-restaurant-mobile-menu-runtime="true"
                ref={sentinelRef}
                style={{ display: "none" }}
            />
            {header
                ? createPortal(
                    <button
                        aria-controls={navigationId}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? labels.closeMenu : labels.openMenu}
                        className="restaurant-mobile-menu-toggle"
                        onClick={(event) => {
                            event.stopPropagation();
                            setIsOpen((current) => !current);
                        }}
                        onPointerDown={(event) => event.stopPropagation()}
                        ref={toggleRef}
                        type="button"
                    >
                        <span />
                        <span />
                        <span />
                    </button>,
                    header,
                )
                : null}
        </>
    );
}
